import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

const VerifyEmail = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [status, setStatus] = useState('loading'); // loading, success, error
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setMessage('Invalid verification link');
            return;
        }

        const verifyEmail = async () => {
            try {
                await api.get(`/auth/verify-email?token=${token}`);
                setStatus('success');
                setMessage('Your email has been verified successfully!');
                toast.success('Email verified!');
            } catch (error) {
                setStatus('error');
                setMessage(error.response?.data?.detail || 'Verification failed');
                toast.error('Verification failed');
            }
        };

        verifyEmail();
    }, [token]);

    return (
        <>
            <div className="flex flex-col items-center mb-6">
                <BrandLogo className="h-16 w-16 rounded-lg shadow-lg" />
            </div>

            <Card className="border border-white/10 bg-white/5 backdrop-blur-md shadow-xl">
                <CardContent className="pt-6">
                    <div className="text-center py-4">
                        {status === 'loading' && (
                            <>
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                                    <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
                                </div>
                                <h3 className="text-lg font-medium text-white mb-2">Verifying Email...</h3>
                                <p className="text-slate-400">Please wait while we verify your email address.</p>
                            </>
                        )}

                        {status === 'success' && (
                            <>
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                                    <CheckCircle className="h-8 w-8 text-green-400" />
                                </div>
                                <h3 className="text-lg font-medium text-white mb-2">Email Verified!</h3>
                                <p className="text-slate-400 mb-6">{message}</p>
                                <Button
                                    onClick={() => navigate('/projects')}
                                    className="bg-gradient-to-r from-[#9b00e8] to-[#ff4500] hover:from-[#8800d4] hover:to-[#e63e00]"
                                >
                                    Continue to Dashboard
                                </Button>
                            </>
                        )}

                        {status === 'error' && (
                            <>
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                                    <XCircle className="h-8 w-8 text-red-400" />
                                </div>
                                <h3 className="text-lg font-medium text-white mb-2">Verification Failed</h3>
                                <p className="text-slate-400 mb-6">{message}</p>
                                <div className="space-y-2">
                                    <Link to="/login">
                                        <Button className="w-full bg-gradient-to-r from-[#9b00e8] to-[#ff4500]">
                                            Go to Login
                                        </Button>
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                </CardContent>
            </Card>
        </>
    );
};

export default VerifyEmail;
